import { useCallback } from 'react'

import { useForm, useStore } from '@tanstack/react-form'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { routes } from 'commons/constants/routes'
import { Request } from 'commons/utils/request'
import { Toast } from 'ui/components'

import { handlerServerError } from '@orbit/errors'

import { DepositEdit, DepositEditForm } from './types'

export const useMRWebPageDepositsManage = (MRId: number, bankAccountId: number) => {
  const navigate = useNavigate()
  const { type, account } = useSearch({ from: '/_app/mr/$id/$bankAccountId/deposits-edit' })

  const payload: any = {
    bankAccountId
  }
  if (type === 'custom') {
    payload.reportCustomId = MRId
  } else {
    payload.reportMonthlyId = MRId
  }

  const { data } = useQuery<DepositEdit>({
    queryKey: ['bank-source-setup-mode'],
    queryFn: () => Request.post(Request.urls.bank.depositsDataBankSourceSetupMode, payload),
    structuralSharing: true,
    staleTime: 1000,
    placeholderData: keepPreviousData
  })

  const formApi = useForm({
    defaultValues: data?.form
  })

  const { mutate: updateDeposits, isPending: isSubmitting } = useMutation<void, Error, DepositEditForm>({
    mutationFn: (body) => Request.post(Request.urls.bank.depositsDataBankSource, body),
    onError: (error) => handlerServerError(error),
    onSuccess: async () => {
      Toast.successSmall('Deposits has been saved.')
      if (type === 'custom') {
        navigate({
          to: routes.bank.hrr.details,
          params: { id: MRId.toString() },
          hash: account && account.toString() && account.toString().length > 0 ? `bank-account-${account}` : ''
        })
      } else {
        navigate({ to: '/mr/$id', params: { id: MRId.toString() } })
      }
    }
  })

  const onCancel = () => {
    if (type === 'custom') {
      navigate({
        to: routes.bank.hrr.details,
        params: {
          id: MRId.toString()
        },
        hash: account && account.toString() && account.toString().length > 0 ? `bank-account-${account}` : ''
      })
    } else {
      navigate({ to: '/mr/$id', params: { id: MRId.toString() } })
    }
  }

  const value = useStore(formApi.store, (s) => s.values)
  const onSubmit = useCallback(() => updateDeposits(value), [updateDeposits, value, navigate, MRId])

  return {
    data,
    onCancel,
    onSubmit,
    isSubmitting,
    formApi
  }
}
